import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";

import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 3,
  },
  colors: {
    primary: {
      main: "#001E50",
      dark: "#5F6467",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#00B0F0",
      dark: "#875735",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    info: {
      main: "#0082D6",
      light: "#D6EBF9",
    },
    text: {
      primary: "#323537",
      secondary: "#070708",
      hint: "#00B0F0",
    },

    background: "#FFFFFF",
    backgroundHeader: "#FFFFFF",
    elementHeader: "#F2F4F6",
    border: "#8B9196",
    hover: "#5F6467",
    placeholder: "#999999",
    transparent: "#00000000",
    divider: "#DFE4E8",
  },
};
// const svgIcons = {
//   circleRemove: {
//     d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
//   },
// }

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const volkswagen = {
  themeName: "Volkswagen",
  brand: BrandsEnum.Volkswagen,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.primary.main,
      paper: variables.colors.elementHeader,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.elementHeader,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("VWTextWeb-Regular"),
    color: variables.colors.text.primary,
    h1: {
      fontFamily: getFontFamily("VWHeadWeb-Bold"),
      fontSize: "2.25rem",
      lineHeight: "2.75rem",
      color: variables.colors.text.secondary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontFamily: getFontFamily("VWHeadWeb-Bold"),
      fontSize: "1.75rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 400,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontFamily: getFontFamily("VWHeadWeb-Bold"),
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      color: variables.colors.text.secondary,
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    h4: {
      fontFamily: getFontFamily("VWHeadWeb-Bold"),
      color: variables.colors.text.secondary,
      fontWeight: 400,
      fontSize: "1.125rem",
      lineHeight: "1.625rem",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
      },
    },
    h5: {
      fontSize: "1.25rem",
      color: variables.colors.text.secondary,
      fontFamily: getFontFamily("VWHeadWeb-Bold"),
      fontWeight: 400,
      [theme.breakpoints.up("md")]: {
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
      },
    },
    h6: {
      color: variables.colors.text.secondary,
      fontFamily: getFontFamily("VWHeadWeb-Bold"),
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.secondary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: "#F2F4F6",
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#F2F4F6",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `1px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      addText: {
        fontWeight: 600,
      },
    },
    price: {
      color: variables.colors.text.secondary,
      fontWeight: 600,
    },
    header: {
      fontWeight: 600,
    },
    addText: {
      fontWeight: 600,
    },
  },
  checkoutLinks: {
    color: variables.colors.hover,
    "& :hover": {
      color: variables.colors.primary.main,
    },
  },
  CheckoutPage: {
    links: {
      color: variables.colors.hover,
      "& :hover": {
        color: variables.colors.primary.main,
      },
    },
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
        borderRadius: 50,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
        fontWeight: 400,
        "a&": {
          fontWeight: 400,
          "&:hover": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.hover,
            "& span": {
              textDecoration: "none",
              color: variables.colors.primary.contrastText,
            },
          },
          "&:active": {
            color: variables.colors.primary.contrastText,
            backgroundColor: variables.colors.primary.dark,
            "& span": {
              textDecoration: "none",
              color: variables.colors.primary.contrastText,
            },
          },
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          textDecoration: "none",
          "& span": {
            color: variables.colors.primary.contrastText,
          },
        },
      },
      containedPrimary: {
        fontWeight: 400,
      },
      outlinedSecondary: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.hover,
        },
        "&:active": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.hover,
        },
        color: variables.colors.primary.main,
        border: "1px solid " + variables.colors.primary.main,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&": {
          "&:hover": {
            border: "1px solid " + variables.colors.primary.main,
            color: variables.colors.primary.main,
            backgroundColor: variables.colors.hover,
            "& span": {
              textDecoration: "none",
              color: variables.colors.primary.main,
            },
          },
          "&:active": {
            border: "1px solid " + variables.colors.primary.main,
            color: variables.colors.primary.main,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
              color: variables.colors.text.hint,
            },
          },
          color: variables.colors.primary.main,
          border: "1px solid " + variables.colors.primary.main,
          "& span": {
            color: variables.colors.primary.main,
          },
        },
      },
      text: {
        "&:hover": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          color: variables.colors.text.hint,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.text.primary,
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: variables.colors.text.secondary,
        fontFamily: getFontFamily("VWHeadWeb-Bold"),
        "&$focused": {
          color: variables.colors.border,
        },
      },
    },
    MuiInputLabel: {
      formControl: {
        position: "relative" as PositionProperty,
        transform: "none",
        color: variables.colors.text.secondary,
        fontFamily: getFontFamily("VWHeadWeb-Bold"),
        lineHeight: 1.75, // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.transparent,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 1,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.elementHeader,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.elementHeader,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          color: variables.colors.hover,
          textDecoration: "underline",
          "&:hover": {
            color: variables.colors.primary.main,
          },
          "&:active": {
            color: variables.colors.primary.main,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontFamily: getFontFamily("VWHeadWeb-Bold"),
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiPickersDay: {
      dayDisabled: {
        color: variables.colors.divider,
      },
    },
    MuiPickersCalendarHeader: {
      dayLabel: {
        color: variables.colors.primary.main,
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.primary.main,
        border: "1px solid " + variables.colors.border,
        "&$selected": {
          color: variables.colors.primary.main,
          borderColor: variables.colors.primary.main,
          backgroundColor: variables.colors.background,
          borderBottom: "5px solid " + variables.colors.primary.main,
          "&:hover": {
            backgroundColor: variables.colors.elementHeader,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.elementHeader,
        },
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
  priceText: {
    totalPriceColor: variables.colors.primary.main,
    fontWeight: 400,
  },
  icons: {
    selectIcon: <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' />,
    infoIcon: (
      <svg id='Info' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
        <g id='Icons' transform='translate(0.4 0.4)'>
          <path
            id='Path_11008'
            data-name='Path 11008'
            d='M11.6,21.6a10,10,0,1,1,10-10A10.043,10.043,0,0,1,11.6,21.6Zm0-19.185A9.185,9.185,0,1,0,20.785,11.6,9.208,9.208,0,0,0,11.6,2.415Z'
            fill='#0082d6'
          />
          <circle
            id='Ellipse_1526'
            data-name='Ellipse 1526'
            cx='0.707'
            cy='0.707'
            r='0.707'
            transform='translate(10.893 5.241)'
            fill='#0082d6'
          />
          <rect
            id='Rectangle_2815'
            data-name='Rectangle 2815'
            width='0.815'
            height='8.967'
            transform='translate(11.165 8.72)'
            fill='#0082d6'
          />
        </g>
        <path id='Path_11101' data-name='Path 11101' d='M0,0H24V24H0Z' opacity='0' />
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    helpIcon: (
      <svg xmlns='http://www.w3.org/2000/svg' width='20.25' height='20.25' viewBox='0 0 20.25 20.25'>
        <g id='Icons' transform='translate(-1.475 -1.475)'>
          <path
            id='Path_10969'
            data-name='Path 10969'
            d='M11.6,21.6a10,10,0,1,1,10-10A10.043,10.043,0,0,1,11.6,21.6Zm0-19.185A9.185,9.185,0,1,0,20.785,11.6,9.208,9.208,0,0,0,11.6,2.415Z'
            transform='translate(0 0)'
            fill='#001e50'
            stroke='#001e50'
            strokeWidth='0.25'
          />
          <path
            id='Path_10970'
            data-name='Path 10970'
            d='M18.1,17.452h-.815V17.18c0-1.576.054-1.957,1.9-3.37,1.63-1.3,2.228-3.1,1.413-4.348a2.915,2.915,0,0,0-2.554-1.2A2.614,2.614,0,0,0,15.707,9.68L15,9.245A3.571,3.571,0,0,1,18.043,7.4,3.9,3.9,0,0,1,21.3,8.973c1.087,1.63.38,3.913-1.576,5.435-1.576,1.25-1.576,1.359-1.576,2.717Z'
            transform='translate(-6.117 -2.645)'
            fill='#001e50'
            stroke='#001e50'
            strokeWidth='0.25'
          />
          <circle
            id='Ellipse_1522'
            data-name='Ellipse 1522'
            cx='0.707'
            cy='0.707'
            r='0.707'
            transform='translate(10.893 16.22)'
            fill='#001e50'
            stroke='#001e50'
            strokeWidth='0.25'
          />
        </g>
      </svg>
    ),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    checkmarkIcon: (
      <svg id='check_circle_black_24dp' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
        <path id='Path_56' data-name='Path 56' d='M0,0H24V24H0Z' fill='none' />
        <circle id='Ellipse_1518' data-name='Ellipse 1518' cx='10' cy='10' r='10' transform='translate(2 1.516)' fill='none' />
        <path
          id='checkmark'
          d='M7.2,18.66a.607.607,0,0,1-.384-.137L0,12.931.768,12,7.08,17.171,19.006,0,20,.691,7.7,18.4a.609.609,0,0,1-.414.254A.508.508,0,0,1,7.2,18.66Z'
          transform='translate(2 2.187)'
          fill='#029640'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg
      id="check_circle_black_24dp"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <path id="Path_56" data-name="Path 56" d="M0,0H24V24H0Z" fill="none" />
      <circle
        id="Ellipse_1518"
        data-name="Ellipse 1518"
        cx="10"
        cy="10"
        r="10"
        transform="translate(2 1.516)"
        fill="none"
      />
      <path
        id="checkmark"
        d="M7.2,18.66a.607.607,0,0,1-.384-.137L0,12.931.768,12,7.08,17.171,19.006,0,20,.691,7.7,18.4a.609.609,0,0,1-.414.254A.508.508,0,0,1,7.2,18.66Z"
        transform="translate(2 2.187)"
        fill="#029640"
      />
    </svg>`),
    crossIcon: (
      <svg id='Icon-Close' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>
        <g id='Rectangle_29' data-name='Rectangle 29' stroke='#999' strokeWidth='1' opacity='0'>
          <rect width='24' height='24' stroke='none' />
          <rect x='0.5' y='0.5' width='23' height='23' fill='none' />
        </g>
        <g id='close_black_24dp'>
          <path id='Path_4601' data-name='Path 4601' d='M0,0H24V24H0Z' fill='none' />
          <path
            id='close'
            d='M16,.841,15.159,0,8,7.159.841,0,0,.841,7.159,8,0,15.159.841,16,8,8.841,15.159,16,16,15.159,8.841,8Z'
            transform='translate(4 3.516)'
            fill='#999'
          />
        </g>
      </svg>
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#D6F1FB",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  popup: {
    root: {
      fontFamily: getFontFamily("VWTextWeb-Regular"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#001E50",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("VWTextWeb-Bold"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.main,
      backgroundColor: variables.colors.background,
    },
  },
  header: {
    "& *": {
      color: variables.colors.primary.contrastText,
    },
  },
  coverageSelect: {
    root: {
      backgroundColor: variables.colors.background,
      CoverageItemText: {
        color: variables.colors.text.secondary,
      },
    },
    selected: {
      backgroundColor: variables.colors.elementHeader,
    },
    descriptionCard: {
      backgroundColor: variables.colors.elementHeader,
      borderColor: variables.colors.elementHeader,
    },
  },
  statusIndicator: {
    borderRadius: 2,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(volkswagen);
